import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { RecentImage, UploadedImage } from '../api/endpoints/uploads';

export type ImagePickerResult =
  | { cancelled: true }
  | {
      cancelled: false;
      filename: string;
      url: string;
      hash: string | null;
      description?: string;
      tags?: string[];
      source: 'recent' | 'upload';
    };

export interface ImagePickerRequest {
  purpose?: string;
  hint?: string;
}

interface InternalPending {
  request: ImagePickerRequest;
  resolve: (result: ImagePickerResult) => void;
}

interface ImagePickerContextValue {
  /** Open the picker and return a promise that resolves on pick/upload/cancel. */
  requestImageChoice: (request?: ImagePickerRequest) => Promise<ImagePickerResult>;
  /** Internal state for the overlay component. */
  pending: ImagePickerRequest | null;
  /** Internal: called by the overlay when the user picks an existing image. */
  resolveWithRecent: (image: RecentImage) => void;
  /** Internal: called by the overlay when the user uploads a fresh image. */
  resolveWithUpload: (uploaded: UploadedImage) => void;
  /** Internal: called by the overlay when the user cancels. */
  resolveWithCancel: () => void;
}

const ImagePickerContext = createContext<ImagePickerContextValue | null>(null);

export function ImagePickerProvider({ children }: { children: ReactNode }) {
  const pendingRef = useRef<InternalPending | null>(null);
  const [pendingRequest, setPendingRequest] = useState<ImagePickerRequest | null>(null);

  const requestImageChoice = useCallback((request: ImagePickerRequest = {}) => {
    // Only one active picker at a time. Resolve any prior pick as cancelled to
    // avoid leaking a hanging promise.
    if (pendingRef.current) {
      pendingRef.current.resolve({ cancelled: true });
      pendingRef.current = null;
    }
    return new Promise<ImagePickerResult>((resolve) => {
      pendingRef.current = { request, resolve };
      setPendingRequest(request);
    });
  }, []);

  const finish = useCallback((result: ImagePickerResult) => {
    const p = pendingRef.current;
    pendingRef.current = null;
    setPendingRequest(null);
    if (p) p.resolve(result);
  }, []);

  const resolveWithRecent = useCallback((image: RecentImage) => {
    finish({
      cancelled: false,
      filename: image.filename,
      url: image.url,
      hash: image.hash,
      description: image.description,
      tags: image.tags,
      source: 'recent',
    });
  }, [finish]);

  const resolveWithUpload = useCallback((uploaded: UploadedImage) => {
    finish({
      cancelled: false,
      filename: uploaded.filename,
      url: uploaded.url,
      hash: uploaded.hash,
      source: 'upload',
    });
  }, [finish]);

  const resolveWithCancel = useCallback(() => finish({ cancelled: true }), [finish]);

  const value = useMemo<ImagePickerContextValue>(
    () => ({
      requestImageChoice,
      pending: pendingRequest,
      resolveWithRecent,
      resolveWithUpload,
      resolveWithCancel,
    }),
    [requestImageChoice, pendingRequest, resolveWithRecent, resolveWithUpload, resolveWithCancel],
  );

  return <ImagePickerContext.Provider value={value}>{children}</ImagePickerContext.Provider>;
}

export function useImagePicker() {
  const ctx = useContext(ImagePickerContext);
  if (!ctx) throw new Error('useImagePicker must be used inside ImagePickerProvider');
  return ctx;
}
